import { List } from 'immutable';

import { type OpenOverlayAction } from 'src/types';

import * as Constants from '../constants';
import { ApiActions } from '../constants/ApiActions';
import { AppActions } from '../constants/App';
import { AudioPlayerActions, AudioPlayerKeys } from '../constants/AudioPlayer';
import { MiniCoachmarkKeys } from '../constants/MiniCoachmark';
import { OVERLAY_ACTIONS, OVERLAY_CONFIG } from '../constants/Overlay';
import { UsageKeys } from '../constants/Usage';
import { Map } from '../lib/ImmutableUtils';
import { getOverlayId } from '../lib/Overlay';
import { createReducer } from '../lib/ReducerUtils';
import { getNow } from '../lib/TimeUtils';

const INITIAL_STATE = Map({
  [UsageKeys.OVERLAYS]: Map<$TSFixMe>(),
  [UsageKeys.RESOURCE_URL]: null as $TSFixMe | null,
  [UsageKeys.TIMESTAMP]: null as $TSFixMe | null,
  [UsageKeys.PROGRESS_TIME]: null as number | null,
  [UsageKeys.TOTAL_LISTENING_TIME]: null as number | null,
  [UsageKeys.SESSION_START_TIME]: getNow(),
  [MiniCoachmarkKeys.LAST_REPLAY_DATE]: null as $TSFixMe | null,
  [UsageKeys.SKIP_USAGE]: Map<$TSFixMe>(),
});
export type UsageState = typeof INITIAL_STATE;

// USAGE REDUCER
export default createReducer(INITIAL_STATE, {
  [AudioPlayerActions.UPDATE]: audioUpdate,
  [OVERLAY_ACTIONS.OPEN_OVERLAY]: openOverlay,
  [Constants.Playback.ActionTypes.REPLAY_TRACK_SUCCESS]: replayTrackSuccess,
  [AppActions.APPLICATION_INITIALIZE]: applicationInitialize,
  [ApiActions.LOGIN_SUCCESS]: clearUsage,
  [ApiActions.ANON_LOGIN_SUCCESS]: clearUsage,
  [ApiActions.DEVICE_LOGIN_SUCCESS]: clearUsage,
  [ApiActions.REGISTER_SUCCESS]: clearUsage,
  [Constants.Playback.ActionTypes.REGISTER_SKIP_TRACK]: skipTrackSuccess,
});

// CASE FUNCTIONS

function audioUpdate(state: UsageState, { payload }: $TSFixMe): UsageState {
  const currentUrl = payload[AudioPlayerKeys.RESOURCE_URL];
  const lastUrl = state.get(UsageKeys.RESOURCE_URL);

  const currentPlaytime = payload[AudioPlayerKeys.CURRENT_TIME];
  const lastPlaytime = state.get(UsageKeys.PROGRESS_TIME);

  const currentTimestamp = payload[AudioPlayerKeys.CURRENT_TIMESTAMP];
  const lastTimestamp = state.get(UsageKeys.TIMESTAMP);

  if (currentUrl !== lastUrl) {
    return state.merge({
      [UsageKeys.RESOURCE_URL]: currentUrl,
      [UsageKeys.TIMESTAMP]: currentTimestamp,
      [UsageKeys.PROGRESS_TIME]: currentPlaytime,
      [UsageKeys.CURRENT_RESOURCE_PLAYTIME_START]: state.get(UsageKeys.TOTAL_LISTENING_TIME),
    });
  }

  const elapsedPlaytime = Math.max(
    Math.round((currentPlaytime - (lastPlaytime || 0) || 0) * 1000),
    0,
  );
  const elapsedTimestamp = lastTimestamp ? currentTimestamp - lastTimestamp : 0;

  const realElapsedTime = Math.min(elapsedPlaytime, elapsedTimestamp);

  return state.merge({
    [UsageKeys.RESOURCE_URL]: currentUrl,
    [UsageKeys.TIMESTAMP]: currentTimestamp,
    [UsageKeys.PROGRESS_TIME]: currentPlaytime,
    [UsageKeys.TOTAL_LISTENING_TIME]:
      (state.get(UsageKeys.TOTAL_LISTENING_TIME) ?? 0) + realElapsedTime,
  });
}

function openOverlay(
  state: UsageState,
  { payload: { overlayConfig } }: OpenOverlayAction,
): UsageState {
  let stateUpdate = state;
  if (!state.get(UsageKeys.OVERLAYS)) {
    // handle restored state initialized before overlays
    stateUpdate = stateUpdate.setIn([UsageKeys.OVERLAYS], Map());
  }
  const id = getOverlayId(overlayConfig);
  overlayConfig[OVERLAY_CONFIG.ID] = id;

  const oldOverlay = stateUpdate.getIn(
    [UsageKeys.OVERLAYS, id],
    Map({
      sessionShowCount: 0,
      lastShownTime: 0,
    }),
  );

  const updatedOverlay = oldOverlay
    .setIn(['sessionShowCount'], oldOverlay.getIn(['sessionShowCount'], 0) + 1)
    .setIn(['lastShownTime'], new Date().getTime());

  return stateUpdate.setIn([UsageKeys.OVERLAYS, id], updatedOverlay);
}

function replayTrackSuccess(state: UsageState, action: $TSFixMe): UsageState {
  return state.set(MiniCoachmarkKeys.LAST_REPLAY_DATE, action.data.date);
}

function applicationInitialize(state: UsageState): UsageState {
  // clear out sessionShowCount for overlays
  let update = state;
  state.getIn([UsageKeys.OVERLAYS], Map()).forEach((_: never, overlayId: $TSFixMe) => {
    update = update.setIn([UsageKeys.OVERLAYS, overlayId, 'sessionShowCount'], 0);
  });
  return update;
}

function clearUsage(state: UsageState, action: $TSFixMe): UsageState {
  const isUserInitiated = action.isUserInitiated || action.isCustomerServiceLogin;

  if (isUserInitiated) {
    return INITIAL_STATE;
  }

  return state.set(UsageKeys.SESSION_START_TIME, getNow());
}

function skipTrackSuccess(state: UsageState, action: $TSFixMe): UsageState {
  const stationId = action.data.stationId;
  const skips = state.getIn([UsageKeys.SKIP_USAGE, stationId], List()).push(action.data.skip);

  return state.setIn([UsageKeys.SKIP_USAGE, stationId], skips);
}
